import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Gear, Globe, ArrowSquareOut, Bank, ArrowsClockwise, Key, PenNib, Copy } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { SiteContentEditor } from "@/components/SiteContentEditor";
import { toast } from "sonner";

export default function SiteSettings() {
  const [settings, setSettings] = useState(null);
  const [meta, setMeta] = useState([]);
  const [apiKeys, setApiKeys] = useState({});
  const [saving, setSaving] = useState("");

  const reload = () => api.get("/settings").then((r) => { setSettings(r.data.settings); setMeta(r.data.meta); setApiKeys(r.data.api_keys || {}); });
  useEffect(() => { reload(); }, []);

  const toggle = async (key, value) => {
    setSaving(key);
    const prev = settings;
    setSettings({ ...settings, [key]: value });
    try {
      const { data } = await api.put("/settings", { [key]: value });
      setSettings(data);
      toast.success(value ? "Função ativada" : "Função desativada");
    } catch {
      setSettings(prev);
      toast.error("Não foi possível salvar");
    } finally { setSaving(""); }
  };

  if (!settings) return <div className="p-10 text-slate-500">Carregando…</div>;

  return (
    <div className="p-8 max-w-[1000px]" data-testid="site-settings-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Configurações</div>
          <h1 className="font-display text-4xl tracking-tight">Configuração do Site</h1>
          <p className="text-slate-600 mt-2 text-sm max-w-xl">
            Funções, textos e integrações do site público. As alterações valem imediatamente para todos os visitantes.
          </p>
        </div>
        <a href="/" target="_blank" rel="noreferrer" data-testid="settings-open-site" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 border border-[var(--line)] rounded-md px-3 py-2 bg-white">
          <Globe size={16} /> Ver site <ArrowSquareOut size={14} />
        </a>
      </div>

      <Tabs defaultValue="features">
        <TabsList className="mb-6">
          <TabsTrigger value="features" data-testid="settings-tab-features">Funções</TabsTrigger>
          <TabsTrigger value="content" data-testid="settings-tab-content">Textos do site</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="settings-tab-integrations">Integrações</TabsTrigger>
        </TabsList>

        <TabsContent value="features">
          <div className="card-flat divide-y divide-[var(--line)]">
            {meta.map((m) => (
              <div key={m.key} className="flex items-start justify-between gap-6 p-5" data-testid={`setting-row-${m.key}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 flex items-center gap-2">
                    <Gear size={16} className="text-slate-400" /> {m.label}
                    <span className={`status-pill ${settings[m.key] ? "status-ativo" : "status-cancelado"} text-[10px]`} data-testid={`setting-status-${m.key}`}>
                      <span>{settings[m.key] ? "Ativo" : "Desativado"}</span>
                    </span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{m.desc}</div>
                </div>
                <Switch
                  data-testid={`setting-switch-${m.key}`}
                  checked={!!settings[m.key]}
                  disabled={saving === m.key}
                  onCheckedChange={(v) => toggle(m.key, v)}
                />
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="content"><SiteContentEditor /></TabsContent>

        <TabsContent value="integrations">
          <InterCard />
          <ClicksignCard masked={apiKeys} onSaved={reload} />
          <ApiKeysCard masked={apiKeys} onSaved={reload} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ClicksignCard({ masked, onSaved }) {
  const [st, setSt] = useState(null);
  const [token, setToken] = useState("");
  const [secret, setSecret] = useState("");
  const [env, setEnv] = useState(masked?.clicksign_env || "sandbox");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/clicksign/status").then((r) => setSt(r.data)).catch(() => setSt({ error: "Falha ao consultar" }));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (masked?.clicksign_env) setEnv(masked.clicksign_env); }, [masked?.clicksign_env]);

  const save = async () => {
    setBusy(true);
    try {
      const body = { clicksign_env: env };
      if (token.trim()) body.clicksign_access_token = token.trim();
      if (secret.trim()) body.clicksign_webhook_secret = secret.trim();
      await api.put("/settings/api-keys", body);
      toast.success("Configuração da Clicksign salva"); setToken(""); setSecret(""); onSaved(); load();
    } catch { toast.error("Falha ao salvar"); }
    finally { setBusy(false); }
  };

  const ok = st?.configured && st?.connected;
  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/clicksign`;
  return (
    <div className="card-flat p-5 mt-6" data-testid="clicksign-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="eyebrow mb-1">Integração</div>
          <div className="font-display text-2xl tracking-tight flex items-center gap-2">
            <PenNib size={22} className="text-[var(--emerald)]" /> Clicksign — assinatura digital
            {st && (
              <span className={`status-pill ${ok ? "status-ativo" : st.configured ? "status-reprovado" : "status-pendente"} text-[10px]`} data-testid="clicksign-status-pill">
                <span>{ok ? "Conectado" : st.configured ? "Erro de conexão" : "Sem token"}</span>
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-2">
            Gere o <strong>Access Token</strong> em <em>Configurações → API</em> na sua conta Clicksign ({env === "production" ? "app.clicksign.com" : "sandbox.clicksign.com"}).
            Cadastre também o webhook abaixo na Clicksign (eventos <span className="mono">auto_close</span>, <span className="mono">document_closed</span>) e cole aqui o <strong>HMAC secret</strong> gerado.
          </p>
          {st?.error && <div className="text-red-700 mono text-xs break-all mt-2" data-testid="clicksign-error">{st.error}</div>}
          <div className="grid md:grid-cols-2 gap-3 mt-4">
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Ambiente</Label>
              <Select value={env} onValueChange={setEnv}>
                <SelectTrigger data-testid="clicksign-env"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sandbox">Sandbox (testes, sem validade jurídica)</SelectItem>
                  <SelectItem value="production">Produção</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Access Token {masked?.clicksign_access_token && <span className="mono text-slate-400">(atual: {masked.clicksign_access_token})</span>}</Label>
              <Input data-testid="clicksign-token-input" type="password" value={token} onChange={(e) => setToken(e.target.value)} placeholder="Cole o Access Token" className="mono" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500 mb-1.5 block">URL do webhook (cadastre na Clicksign)</Label>
              <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                <span className="mono text-xs truncate flex-1" data-testid="clicksign-webhook-url">{webhookUrl}</span>
                <button onClick={() => { navigator.clipboard.writeText(webhookUrl); toast.success("Copiado"); }} className="text-slate-500 hover:text-slate-900"><Copy size={14} /></button>
              </div>
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500 mb-1.5 block">HMAC secret do webhook {masked?.clicksign_webhook_secret && <span className="mono text-slate-400">(atual: {masked.clicksign_webhook_secret})</span>}</Label>
              <Input data-testid="clicksign-secret-input" type="password" value={secret} onChange={(e) => setSecret(e.target.value)} placeholder="Cole o secret gerado pela Clicksign" className="mono" />
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="clicksign-refresh-btn"><ArrowsClockwise size={14} className="mr-1" /> Testar conexão</Button>
          <Button size="sm" className="btn-primary" disabled={busy} onClick={save} data-testid="clicksign-save-btn">Salvar</Button>
        </div>
      </div>
    </div>
  );
}

function ApiKeysCard({ masked, onSaved }) {
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!val.trim()) return;
    setBusy(true);
    try {
      await api.put("/settings/api-keys", { transparencia_api_key: val.trim() });
      toast.success("Chave salva"); setVal(""); onSaved();
    } catch { toast.error("Falha ao salvar chave"); }
    finally { setBusy(false); }
  };
  return (
    <div className="card-flat p-5 mt-6" data-testid="apikeys-card">
      <div className="eyebrow mb-1">Integração</div>
      <div className="font-display text-2xl tracking-tight flex items-center gap-2">
        <Key size={22} className="text-[var(--emerald)]" /> Portal da Transparência (gov.br)
        <span className={`status-pill ${masked?.transparencia_api_key ? "status-ativo" : "status-pendente"} text-[10px]`} data-testid="transparencia-status-pill">
          <span>{masked?.transparencia_api_key ? "Configurada" : "Sem chave"}</span>
        </span>
      </div>
      <p className="text-sm text-slate-500 mt-2">
        Chave gratuita usada na aba <strong>Análise por CPF</strong>. Cadastre-se em{" "}
        <a className="underline" href="https://portaldatransparencia.gov.br/api-de-dados/cadastrar-email" target="_blank" rel="noreferrer">portaldatransparencia.gov.br/api-de-dados</a> e cole a chave recebida por e-mail.
        {masked?.transparencia_api_key && <span className="block mono text-xs mt-1">Chave atual: {masked.transparencia_api_key}</span>}
      </p>
      <div className="flex gap-2 mt-3 max-w-xl">
        <Input data-testid="transparencia-key-input" value={val} onChange={(e) => setVal(e.target.value)} placeholder="Cole aqui a chave da API" className="mono" />
        <Button className="btn-primary" disabled={busy || !val.trim()} onClick={save} data-testid="transparencia-key-save">Salvar</Button>
      </div>
    </div>
  );
}

function InterCard() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/inter/status").then((r) => setSt(r.data)).catch(() => setSt({ error: "Falha ao consultar" }));
  useEffect(() => { load(); }, []);

  const registerWebhook = async () => {
    setBusy(true);
    try {
      await api.put("/inter/webhook", { url: `${process.env.REACT_APP_BACKEND_URL}/api/webhooks/inter` });
      toast.success("Webhook registrado no Banco Inter");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao registrar webhook"); }
    finally { setBusy(false); }
  };

  if (!st) return null;
  const ok = st.configured && st.connected;
  return (
    <div className="card-flat p-5" data-testid="inter-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-1">Integração</div>
          <div className="font-display text-2xl tracking-tight flex items-center gap-2">
            <Bank size={22} className="text-[var(--emerald)]" /> Banco Inter
            <span className={`status-pill ${ok ? "status-ativo" : st.configured ? "status-reprovado" : "status-pendente"} text-[10px]`} data-testid="inter-status-pill">
              <span>{ok ? "Conectado" : st.configured ? "Erro de conexão" : "Sem certificado"}</span>
            </span>
          </div>
          <div className="text-sm text-slate-500 mt-2 space-y-1">
            <div>Ambiente: <strong>{st.env === "production" ? "Produção" : "Sandbox (homologação)"}</strong> · Conta: <span className="mono">{st.account || "—"}</span></div>
            <div>Credenciais: {st.has_credentials ? "✓" : "✗"} · Certificado mTLS: {st.has_cert ? "✓" : "✗ (envie .crt e .key)"}</div>
            {st.webhook_url && <div>Webhook: <span className="mono text-xs">{st.webhook_url}</span></div>}
            {st.error && <div className="text-red-700 mono text-xs break-all" data-testid="inter-error">{st.error}</div>}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <Button size="sm" variant="outline" onClick={load} data-testid="inter-refresh-btn"><ArrowsClockwise size={14} className="mr-1" /> Testar conexão</Button>
          <Button size="sm" className="btn-primary" disabled={!ok || busy} onClick={registerWebhook} data-testid="inter-webhook-btn">Registrar webhook</Button>
        </div>
      </div>
    </div>
  );
}
